import React, { ChangeEvent, useEffect, useState } from 'react';
import { FormControl, Checkbox } from '@uhg-harmony/uitk-react';
import styled from 'styled-components';
import { getCombinedFontsForTheme } from 'builder/util/getCombinedFonts';
import {
  StyledClose,
  StyledPickerHeader,
  StyledPickerPopover,
  StyledPickerWrapper,
} from 'builder/components/GradientColorPicker/GradientColorPicker';
import { ColorButton } from 'builder/components/ColorButton/ColorButton';
import {
  getBold,
  getItalic,
  getStrikeThrough,
  getUnderline,
  MainLabelStyles,
  StyledIcon,
} from 'builder/components/WidgetConfigurations/Typography/Typography';
import { ColorButtonWrapper } from 'builder/components/WidgetConfigurations/Background/Background';
import GradientColorPicker from 'builder/components/GradientColorPicker/GradientColorPicker';
import { FontSelect } from 'builder/components/WidgetConfigurations/Accordion/AccordionTypography';
import { defaultTheme as theme } from 'shared/theme/theme';
import {
  IconBold,
  IconItalic,
  IconStrikethrough,
  IconUnderline,
} from '@tabler/icons-react';
import {
  FontSizeInput,
  FontSizeInputWrapper,
  FontSizeUnitStyle,
  handleNumberInput,
} from 'builder/components/WidgetConfigurations/Typography/Typography';

const FontDropdowns = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 1rem;
`;

const StyledWrapper = styled.div`
  padding-top: 0.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const TextIconWrapper = styled.div`
  cursor: pointer;
  font-family: OptumSans, sans-serif;
  text-align: center;
  padding-right: 5px;
  border-radius: 7px;
  &:hover {
    background: #d9f6fa;
  }
`;

export const StyledCustomIcon = styled.span`
  height: 1em;
`;

const NavStyleWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
`;

export interface NavigationTypographyProps<T> {
  handleFontSize: (data: string) => void;
  handleFont: (data: string) => void;
  handleTextColor: (data: string) => void;
  handleHoverTextColor: (data: string) => void;
  handleHoverTextColorCheckbox: (data: boolean) => void;
  handleBold: (data: boolean) => void;
  handleItalic: (data: boolean) => void;
  handleUnderline: (data: boolean) => void;
  handleStrikeThrough: (data: boolean) => void;
  widgetState: Widget<T>;
  pages: Array<Page>;
  site?: Site;
  updateSite?: (site: Site) => void;
  saveSite?: () => void;
  color: string;
  disabled?: boolean;
  hideTextColor?: boolean;
}

export const NavigationTypography: React.FC<NavigationTypographyProps<any>> = (
  navigationTypographyProps: NavigationTypographyProps<any>
) => {
  const {
    handleFontSize,
    handleFont,
    handleBold,
    handleItalic,
    handleUnderline,
    handleStrikeThrough,
    handleTextColor,
    handleHoverTextColor,
    handleHoverTextColorCheckbox,
    widgetState,
    site,
    updateSite,
    saveSite,
    disabled,
  } = navigationTypographyProps;
  const { font, fontSize, textColor, hoverTextColor } = widgetState.config;

  const { currentTab } = widgetState.config;

  const [pickerAnchorEl, setPickerAnchorEl] = useState<HTMLDivElement | null>(
    null
  );
  const [
    hoverPickerAnchorEl,
    setHoverPickerAnchorEl,
  ] = useState<HTMLDivElement | null>(null);
  const [
    isDifferentTextColorOnHover,
    setIsDifferentTextColorOnHover,
  ] = useState<boolean>(widgetState.config.isDifferentTextColorOnHover);
  const pickerOpen = Boolean(pickerAnchorEl);
  const hoverPickerOpen = Boolean(hoverPickerAnchorEl);
  const pickerId = pickerOpen
    ? 'simple-popover-navigation-typography'
    : undefined;
  const hoverPickerId = hoverPickerOpen ? 'hover-text-color-picker' : undefined;
  const [_color, setColor] = useState(widgetState.config?.iconBackgroundColor);
  const [bold, setBold] = useState(getBold(widgetState, currentTab));
  const [italic, setItalic] = useState(getItalic(widgetState, currentTab));
  const [underLine, setUnderLine] = useState(
    getUnderline(widgetState, currentTab)
  );
  const [strikeThrough, setStrikeThrough] = useState(
    getStrikeThrough(widgetState, currentTab)
  );

  useEffect(() => {
    setIsDifferentTextColorOnHover(
      widgetState.config.isDifferentTextColorOnHover
    );
    setBold(getBold(widgetState, currentTab));
    setItalic(getItalic(widgetState, currentTab));
    setUnderLine(getUnderline(widgetState, currentTab));
    setStrikeThrough(getStrikeThrough(widgetState, currentTab));
  }, [widgetState.config]);

  const handlePickerClose = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setPickerAnchorEl(null);
    setHoverPickerAnchorEl(null);
  };

  const handleFontChange = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    handleFont(e.target.value);
  };

  const handleFontSizeChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    handleFontSize(e.target.value);
  };

  const handleColorEditClick = (e: React.MouseEvent<any>) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.id === 'hover-text-color'
      ? setHoverPickerAnchorEl(e.currentTarget)
      : setPickerAnchorEl(e.currentTarget);
  };

  const handlePickerClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsDifferentTextColorOnHover(e.target.checked);
    handleHoverTextColorCheckbox(e.target.checked);
  };

  const handleBoldChange = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    setBold(!bold);
    setColor(theme.colors.aux.skyBlue);
    handleBold(!bold);
  };

  const handleItalicChange = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    setItalic(!italic);
    setColor(theme.colors.aux.skyBlue);
    handleItalic(!italic);
  };

  const handleUnderLineChange = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    setUnderLine(!underLine);
    setColor(theme.colors.aux.skyBlue);
    handleUnderline(!underLine);
  };

  const handleStrikeThroughChange = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    setStrikeThrough(!strikeThrough);
    setColor(theme.colors.aux.skyBlue);
    handleStrikeThrough(!strikeThrough);
  };

  return (
    <>
      <FontDropdowns>
        <MainLabelStyles>Font</MainLabelStyles>
        <FormControl
          data-test-id="font-select-form-control"
          disabled={disabled}
          id="controlled-select"
        >
          <FontSelect
            data-test-id="font-select-dropdown"
            onChange={handleFontChange}
            value={font}
          >
            {Object.keys(getCombinedFontsForTheme()).map(f => {
              return (
                <option value={f} key={f}>
                  {f}
                </option>
              );
            })}
          </FontSelect>
        </FormControl>
        <FontSizeInputWrapper>
          <FontSizeInput
            id="navigation-typography-font-size"
            value={fontSize}
            onChange={handleFontSizeChange}
            onKeyDown={handleNumberInput}
            data-test-id="navigation-typography-font-size"
            type="number"
            min={0}
          />
          <FontSizeUnitStyle>px</FontSizeUnitStyle>
        </FontSizeInputWrapper>
      </FontDropdowns>
      <NavStyleWrapper>
        <MainLabelStyles>Style</MainLabelStyles>
        <TextIconWrapper>
          <StyledIcon
            disabled={disabled}
            selected={bold}
            data-test-id="bold-icon"
            title="Bold"
            onClick={e => {
              !disabled && handleBoldChange(e);
            }}
          >
            <IconBold />
          </StyledIcon>
        </TextIconWrapper>
        <TextIconWrapper>
          <StyledIcon
            disabled={disabled}
            selected={italic}
            data-test-id="italic-icon"
            title="Italic"
            onClick={e => {
              !disabled && handleItalicChange(e);
            }}
          >
            <IconItalic />
          </StyledIcon>
        </TextIconWrapper>
        <TextIconWrapper>
          <StyledIcon
            disabled={disabled}
            selected={underLine}
            data-test-id="underline-icon"
            title="Underline"
            onClick={e => {
              !disabled && handleUnderLineChange(e);
            }}
          >
            <IconUnderline />
          </StyledIcon>
        </TextIconWrapper>
        <TextIconWrapper>
          <StyledIcon
            disabled={disabled}
            selected={strikeThrough}
            data-test-id="strikethrough-icon"
            title="Strikethrough"
            onClick={e => {
              !disabled && handleStrikeThroughChange(e);
            }}
          >
            <IconStrikethrough />
          </StyledIcon>
        </TextIconWrapper>
      </NavStyleWrapper>
      <StyledWrapper>
        <MainLabelStyles>Text Color</MainLabelStyles>
        <ColorButtonWrapper
          id="text-color"
          data-test-id="text-color-icon"
          onClick={handleColorEditClick}
        >
          <ColorButton color={textColor} />
        </ColorButtonWrapper>
        <StyledPickerPopover
          data-test-id="widget-color-picker-popover"
          id={pickerId}
          open={pickerOpen}
          onClick={handlePickerClick}
          onClose={handlePickerClose}
          anchorEl={pickerAnchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
        >
          <StyledPickerWrapper>
            <StyledPickerHeader data-test-id="styled-picker-header">
              Regular Text Color Selector
            </StyledPickerHeader>
            <StyledClose
              data-test-id="close-icon"
              onClick={handlePickerClose}
            />
          </StyledPickerWrapper>
          <GradientColorPicker
            value={textColor}
            site={site}
            handleColorChange={handleTextColor}
            onChange={handleTextColor}
            updateSite={updateSite}
            saveSite={saveSite}
            anchorEl={pickerAnchorEl}
          ></GradientColorPicker>
        </StyledPickerPopover>
      </StyledWrapper>
      <StyledWrapper>
        <FormControl id="hover-text-color-checkbox" disabled={disabled}>
          <Checkbox
            onChange={handleCheckboxChange}
            checked={isDifferentTextColorOnHover}
          />
        </FormControl>
        <MainLabelStyles>Different Text Color on Hover</MainLabelStyles>
        {isDifferentTextColorOnHover && (
          <>
            <ColorButtonWrapper
              id="hover-text-color"
              data-test-id="hover-text-color-icon"
              onClick={handleColorEditClick}
            >
              <ColorButton color={hoverTextColor ?? 'black'} />
            </ColorButtonWrapper>
            <StyledPickerPopover
              data-test-id="widget-color-picker-popover"
              id={hoverPickerId}
              open={hoverPickerOpen}
              onClick={handlePickerClick}
              onClose={handlePickerClose}
              anchorEl={hoverPickerAnchorEl}
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'right',
              }}
            >
              <StyledPickerWrapper>
                <StyledPickerHeader data-test-id="styled-picker-header">
                  Hover Text Color Selector
                </StyledPickerHeader>
                <StyledClose
                  data-test-id="close-icon"
                  onClick={handlePickerClose}
                />
              </StyledPickerWrapper>
              <GradientColorPicker
                value={hoverTextColor ?? 'black'}
                site={site}
                handleColorChange={handleHoverTextColor}
                onChange={handleHoverTextColor}
                updateSite={updateSite}
                saveSite={saveSite}
                anchorEl={hoverPickerAnchorEl}
              />
            </StyledPickerPopover>
          </>
        )}
      </StyledWrapper>
    </>
  );
};
